// @ts-nocheck
import React, { useMemo, useState } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  TextField,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enUS from "date-fns/locale/en-US";
import { format } from "date-fns";
import CloseIcon from "@mui/icons-material/Close"; // The "x" icon
import { IconDate } from "../../../../icons/Icon";
import SingleSelectButton from "../../SingleSelectButton";
import SelectWithoutClearButton from "../../SelectWithoutClearButton";

import "./table.scss";

function DaypartDashboardTable({
  data,              // final filtered rows for this tab
  dropdownOptions,   // siteOptions, cafeOptions, menustatusOptions, etc.
  filters,           // local filters object for this tab (dp.filters)
  handleTabFilters,  // (key, value) => updates the hook's state
  plandatesArray,    // date headings
  handleUMExport,
  handleClientExport
}) {
  // For date pickers
  const [initiatedOnLocalVal, setInitiatedOnLocalVal] = useState<Date | null>(null);
  const [submittedOnLocalVal, setSubmittedOnLocalVal] = useState<Date | null>(null);

  // Show Clear & Accept in the popup’s action bar (optional)
  const datePickerSlotProps = {
    actionBar: {
      actions: ["clear", "accept"],
    },
  };

  /** Format a Date object to "dd MMM yyyy". */
  function formatDatePicker(val: Date | null) {
    if (!val) return "";
    return format(val, "dd MMM yyyy");
  }

  // Row‐span logic: group repeated site names
  const getSiteKey = (row) => row.sitename;
  const rowSpanMap = useMemo(() => {
    const spans = {};
    data.forEach((row) => {
      const key = getSiteKey(row);
      spans[key] = (spans[key] || 0) + 1;
    });
    return spans;
  }, [data]);

  const statusClassMap: Record<string, string> = useMemo(
    () => ({
      "N/A": "",
      "Pending": "pending",
      "Pending by UM": "pending",
      "Pending by UM (MP)": "pending",
      "Submitted by UM (MP)": "submitted",
      "Submitted": "submitted",
      "Submitted by UM": "submitted",
      "Saved": "saved",
      "MP Pending": "mp-pending",
      "MR Pending": "mr-pending"
    }),
    []
  );


  return (
    <div className="table-wrapper manager-dashboard">
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
        <TableContainer component={Paper} className="table-inner">
          <MuiTable>
            <TableHead>
              <TableRow>
                {/* ============== SITENAME Filter ============== */}
                <TableCell>
                  <Typography variant="h6" className="table-title">
                    Site Name
                  </Typography>
                  <SelectWithoutClearButton
                    options={dropdownOptions.siteOptions}
                    placeholder="Search"
                    onApply={(val: string[]) => handleTabFilters("sitename", val)}
                    selectedValues={filters.sitename || []}
                  />

                </TableCell>

                {/* ============== CAFENAME Filter ============== */}
                <TableCell>
                  <Typography variant="h6" className="table-title">
                    Cafe Name
                  </Typography>
                  <SelectWithoutClearButton
                    options={dropdownOptions.cafeOptions}
                    placeholder="Search"
                    onApply={(val: string[]) => handleTabFilters("cafename", val)}
                    selectedValues={filters.cafename || []}
                  />
                </TableCell>

                {/* ============== Menustatus Filter (per date) ============== */}
                {plandatesArray.map((dateStr) => (
                  <TableCell key={dateStr}>
                    <Typography variant="h6" className="table-title">
                      {dateStr}
                    </Typography>
                    <SingleSelectButton
                      options={dropdownOptions.menustatusOptions}
                      placeholder="Select"
                      onChange={(e) =>
                        handleTabFilters(`status-${dateStr}`, e.target.value)
                      }
                      value={filters[`status-${dateStr}`] || ""}
                    />
                  </TableCell>
                ))}

                {/* ============== INITIATEDBY Filter ============== */}
                <TableCell>
                  <Typography variant="h6" className="table-title">
                    Initiated By
                  </Typography>
                  <SingleSelectButton
                    options={dropdownOptions.initiatedbyOptions}
                    placeholder="Select"
                    onChange={(e) => handleTabFilters("initiatedby", e.target.value)}
                    value={filters.initiatedby || ""}
                  />
                </TableCell>

                {/* ============== INITIATED ON Date Filter ============== */}
                <TableCell>
                  <Typography variant="h6" className="table-title">Initiated On</Typography>
                  <DatePicker
                    label="Select"
                    value={initiatedOnLocalVal}
                    onChange={(newVal) => {
                      setInitiatedOnLocalVal(newVal);
                      // If user clears date in the pop-up => newVal = null => remove dimension
                      if (!newVal) handleTabFilters("initiatedon", null);
                    }}
                    onAccept={(finalVal) => {
                      if (finalVal) {
                        handleTabFilters("initiatedon", formatDatePicker(finalVal));
                      }
                    }}
                    format="dd MMM yyyy"
                    slotProps={datePickerSlotProps}
                    slots={{
                      openPickerIcon: IconDate,

                      // *** OVERRIDE THE TEXTFIELD SLOT to show a clear ("x") icon
                      textField: (params) => (
                        <TextField
                          {...params}
                          // Merge MUI's default InputProps with our custom endAdornment
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {params.InputProps.endAdornment}
                                {initiatedOnLocalVal && (
                                  <IconButton
                                    onClick={() => {
                                      // Clear the local date & the filter dimension
                                      setInitiatedOnLocalVal(null);
                                      handleTabFilters("initiatedon", null);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            ),
                          }}
                          // We can maintain label & placeholder from params, or override
                          label={params.label}
                        />
                      ),
                    }}
                  />
                </TableCell>

                {/* ============== SUBMITTED ON / LAST MODIFIED DATE Filter ============== */}
                <TableCell>
                  <Typography variant="h6" className="table-title">
                    Submitted On
                  </Typography>
                  <DatePicker
                    label="Select"
                    value={submittedOnLocalVal}
                    onChange={(newVal) => {
                      setSubmittedOnLocalVal(newVal);
                      if (!newVal) handleTabFilters("lastmodifiedon", null);
                    }}
                    onAccept={(finalVal) => {
                      if (finalVal) {
                        handleTabFilters("lastmodifiedon", formatDatePicker(finalVal));
                      }
                    }}
                    format="dd MMM yyyy"
                    slotProps={datePickerSlotProps}
                    slots={{
                      openPickerIcon: IconDate,

                      // *** Similar override for the "Submitted On" date field
                      textField: (params) => (
                        <TextField
                          {...params}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {params.InputProps.endAdornment}
                                {submittedOnLocalVal && (
                                  <IconButton
                                    onClick={() => {
                                      setSubmittedOnLocalVal(null);
                                      handleTabFilters("lastmodifiedon", null);
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </>
                            ),
                          }}
                          label={params.label}
                        />
                      ),
                    }}
                  />
                </TableCell>
                {location.pathname === '/sitemanagerdashboard' && <TableCell>
                  Export
                </TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row, rowIndex) => {
                const currentKey = getSiteKey(row);
                const prevKey = rowIndex > 0 ? getSiteKey(data[rowIndex - 1]) : null;
                const isFirstOccurrence = rowIndex === 0 || currentKey !== prevKey;

                return (
                  <TableRow key={rowIndex}>
                    {/* Row‐span for repeated site names */}
                    {isFirstOccurrence && (
                      <TableCell rowSpan={rowSpanMap[currentKey]}>
                        {row.sitename}
                      </TableCell>
                    )}
                    {/* Cafe name */}
                    <TableCell>{row.cafename}</TableCell>

                    {/* menustatus columns (one per date in plandatesArray) */}
                    {row.statuses.map((statusVal, idx) => (
                      <TableCell key={idx}>
                        <button className={`table-border-btn ${statusClassMap[statusVal] || ""}`}>{statusVal}</button>
                      </TableCell>
                    ))}

                    {/* Initiated By, Initiated On, Last Modified */}
                    <TableCell>{row.initiatedby}</TableCell>
                    {/* <TableCell>{row.initiatedon}</TableCell> */}
                    <TableCell>{row.initiatedonDisplay}</TableCell>

                    <TableCell>{row.lastmodifiedonDisplay}</TableCell>
                    {location.pathname === '/sitemanagerdashboard' &&
                      <TableCell>
                        <div className="link ">
                          <span onClick={() => handleClientExport(row)} >Client</span>
                          <span onClick={() => handleUMExport(row)} >UM</span>
                        </div>
                      </TableCell>
                    }
                  </TableRow>
                );
              })}

              {data.length === 0 && (
                <TableRow>
                  <TableCell colSpan={plandatesArray.length + 5} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </MuiTable>
        </TableContainer>
      </LocalizationProvider>
    </div>
  );
}

export default React.memo(DaypartDashboardTable);